/**
 * Canvas-drawn aircraft silhouettes. Every known ICAO type gets its own
 * top-down shape generated from its real geometry (airframes.ts): length,
 * span, fuselage width, sweep, tail and engine layout, so a 737 MAX, an
 * A350-1000 and a CRJ900 each look like themselves with true proportions.
 * Helicopters, gliders, balloons, drones, fighters and ground vehicles use
 * hand-drawn shapes. Icons are generated on demand (`styleimagemissing`)
 * per type and colour variant, since only the types on screen are needed.
 *
 * Every icon is drawn so the airframe's largest dimension spans
 * ICON_SPAN_PX image pixels; layers.ts turns that into a true-to-scale
 * `icon-size` per zoom level.
 */

import { bucketColor } from './altitude';
import { airframeByKey, type Airframe } from './airframes';

const ICON_PX = 128;
/** Image pixels covered by an airframe's largest dimension (length or span). */
export const ICON_SPAN_PX = 112;

export type PlaneShape =
  | 'jet'
  | 'heavy'
  | 'prop'
  | 'fast'
  | 'heli'
  | 'glider'
  | 'balloon'
  | 'uav'
  | 'ground';

export const PLANE_SHAPES: PlaneShape[] = [
  'jet',
  'heavy',
  'prop',
  'fast',
  'heli',
  'glider',
  'balloon',
  'uav',
  'ground',
];

/** ADS-B emitter category (e.g. "A3") → icon shape. */
export function shapeFor(category: string | null): PlaneShape {
  switch (category) {
    case 'A1':
    case 'A2':
    case 'B4':
      return 'prop';
    case 'A5':
      return 'heavy';
    case 'A6':
    case 'B7':
      return 'fast';
    case 'A7':
      return 'heli';
    case 'B1':
      return 'glider';
    case 'B2':
      return 'balloon';
    case 'B6':
      return 'uav';
    case 'C1':
    case 'C2':
    case 'C3':
      return 'ground';
    default:
      return 'jet'; // A0, A3, A4 and unknown
  }
}

/** Human-readable emitter-category label for the info panel. */
export function categoryLabel(category: string | null): string | null {
  switch (category) {
    case 'A1':
      return 'Light aircraft';
    case 'A2':
      return 'Small aircraft';
    case 'A3':
      return 'Large aircraft';
    case 'A4':
      return 'High-vortex large';
    case 'A5':
      return 'Heavy';
    case 'A6':
      return 'High performance';
    case 'A7':
      return 'Rotorcraft';
    case 'B1':
      return 'Glider';
    case 'B2':
      return 'Lighter-than-air';
    case 'B4':
      return 'Ultralight';
    case 'B6':
      return 'UAV';
    case 'B7':
      return 'Spacecraft';
    case 'C1':
    case 'C2':
    case 'C3':
      return 'Surface vehicle';
    default:
      return null;
  }
}

type Drawer = (ctx: CanvasRenderingContext2D, s: number) => void;

/** High-performance/military: sharp nose, delta wings, small twin tail. */
const drawFast: Drawer = (ctx, s) => {
  ctx.beginPath();
  ctx.moveTo(0, -27 * s);
  ctx.lineTo(2.4 * s, -14 * s);
  ctx.lineTo(19 * s, 12 * s);
  ctx.lineTo(19 * s, 17 * s);
  ctx.lineTo(3 * s, 10 * s);
  ctx.lineTo(3 * s, 15 * s);
  ctx.lineTo(9 * s, 22 * s);
  ctx.lineTo(9 * s, 25 * s);
  ctx.lineTo(0, 21 * s);
  ctx.lineTo(-9 * s, 25 * s);
  ctx.lineTo(-9 * s, 22 * s);
  ctx.lineTo(-3 * s, 15 * s);
  ctx.lineTo(-3 * s, 10 * s);
  ctx.lineTo(-19 * s, 17 * s);
  ctx.lineTo(-19 * s, 12 * s);
  ctx.lineTo(-2.4 * s, -14 * s);
  ctx.closePath();
};

/** Helicopter: cabin + tail boom + 4 rotor blades. */
const drawHeli: Drawer = (ctx, s) => {
  ctx.beginPath();
  ctx.ellipse(0, -4 * s, 6.5 * s, 10 * s, 0, 0, Math.PI * 2);
  // tail boom + fin
  ctx.moveTo(-1.8 * s, 4 * s);
  ctx.lineTo(-1.8 * s, 21 * s);
  ctx.lineTo(-6 * s, 25 * s);
  ctx.lineTo(6 * s, 25 * s);
  ctx.lineTo(1.8 * s, 21 * s);
  ctx.lineTo(1.8 * s, 4 * s);
  ctx.closePath();
  // rotor blades (X)
  const r = 21 * s;
  for (const a of [Math.PI / 4, (3 * Math.PI) / 4]) {
    const dx = Math.cos(a) * r;
    const dy = Math.sin(a) * r;
    const w = 1.6 * s;
    const px = Math.cos(a + Math.PI / 2) * w;
    const py = Math.sin(a + Math.PI / 2) * w;
    ctx.moveTo(-dx + px, -4 * s - dy + py);
    ctx.lineTo(dx + px, -4 * s + dy + py);
    ctx.lineTo(dx - px, -4 * s + dy - py);
    ctx.lineTo(-dx - px, -4 * s - dy - py);
    ctx.closePath();
  }
};

/** Glider: slender fuselage, very long thin straight wings, T-tail. */
const drawGlider: Drawer = (ctx, s) => {
  ctx.beginPath();
  ctx.moveTo(0, -22 * s);
  ctx.bezierCurveTo(1.8 * s, -19 * s, 2 * s, -14 * s, 2 * s, -8 * s);
  ctx.lineTo(30 * s, -6 * s);
  ctx.lineTo(30 * s, -2.4 * s);
  ctx.lineTo(2 * s, -3 * s);
  ctx.lineTo(1.4 * s, 18 * s);
  ctx.lineTo(9 * s, 19 * s);
  ctx.lineTo(9 * s, 22.5 * s);
  ctx.lineTo(-9 * s, 22.5 * s);
  ctx.lineTo(-9 * s, 19 * s);
  ctx.lineTo(-1.4 * s, 18 * s);
  ctx.lineTo(-2 * s, -3 * s);
  ctx.lineTo(-30 * s, -2.4 * s);
  ctx.lineTo(-30 * s, -6 * s);
  ctx.lineTo(-2 * s, -8 * s);
  ctx.bezierCurveTo(-2 * s, -14 * s, -1.8 * s, -19 * s, 0, -22 * s);
  ctx.closePath();
};

/** Balloon: envelope circle + basket. */
const drawBalloon: Drawer = (ctx, s) => {
  ctx.beginPath();
  ctx.arc(0, -6 * s, 14 * s, 0, Math.PI * 2);
  ctx.moveTo(-5 * s, 12 * s);
  ctx.rect(-5 * s, 12 * s, 10 * s, 9 * s);
  // suspension lines
  ctx.moveTo(-7 * s, 6 * s);
  ctx.lineTo(-4 * s, 12 * s);
  ctx.lineTo(4 * s, 12 * s);
  ctx.lineTo(7 * s, 6 * s);
  ctx.closePath();
};

/** UAV/drone: quad arms with rotor discs. */
const drawUav: Drawer = (ctx, s) => {
  ctx.beginPath();
  ctx.ellipse(0, 0, 6 * s, 8 * s, 0, 0, Math.PI * 2);
  for (const [dx, dy] of [
    [-13, -13],
    [13, -13],
    [-13, 13],
    [13, 13],
  ]) {
    // arm
    const ax = dx * s;
    const ay = dy * s;
    const w = 1.8 * s;
    const nx = (-dy / Math.hypot(dx, dy)) * w;
    const ny = (dx / Math.hypot(dx, dy)) * w;
    ctx.moveTo(nx, ny);
    ctx.lineTo(ax + nx, ay + ny);
    ctx.lineTo(ax - nx, ay - ny);
    ctx.lineTo(-nx, -ny);
    ctx.closePath();
    // rotor disc
    ctx.moveTo(ax + 7 * s, ay);
    ctx.arc(ax, ay, 7 * s, 0, Math.PI * 2);
  }
};

/** Surface vehicle: rounded rectangle. */
const drawGround: Drawer = (ctx, s) => {
  ctx.beginPath();
  ctx.roundRect(-8 * s, -13 * s, 16 * s, 26 * s, 5 * s);
};

/** Hand-drawn shapes for non-fixed-wing families, with their extent in drawer units. */
const LEGACY: Partial<Record<Airframe['family'], { draw: Drawer; extent: number }>> = {
  fighter: { draw: drawFast, extent: 54 },
  heli: { draw: drawHeli, extent: 48 },
  glider: { draw: drawGlider, extent: 60 },
  balloon: { draw: drawBalloon, extent: 42 },
  uav: { draw: drawUav, extent: 40 },
  ground: { draw: drawGround, extent: 26 },
};

interface Parts {
  body: Path2D[]; // filled silhouette pieces, painted as one union
  details: Path2D[]; // engine outlines, stroked lightly on top
}

/** Build a fixed-wing airframe's silhouette in image pixels, nose up, centred. */
function airframeParts(a: Airframe): Parts {
  const k = ICON_SPAN_PX / Math.max(a.length, a.span); // px per metre
  const L = a.length * k;
  const B = a.span * k;
  const w = Math.max(a.fuselage * k, 2.2);
  const rad = (deg: number) => (deg * Math.PI) / 180;
  const body: Path2D[] = [];
  const details: Path2D[] = [];
  const nose = -L / 2;
  const tail = L / 2;

  // Fuselage: ogive nose, parallel barrel, tapering tail cone.
  const fus = new Path2D();
  const noseLen = Math.min(w * 1.7, L * 0.18);
  const coneStart = tail - L * 0.22;
  fus.moveTo(0, nose);
  fus.bezierCurveTo(w * 0.42, nose, w / 2, nose + noseLen * 0.45, w / 2, nose + noseLen);
  fus.lineTo(w / 2, coneStart);
  fus.lineTo(w * 0.16, tail);
  fus.lineTo(-w * 0.16, tail);
  fus.lineTo(-w / 2, coneStart);
  fus.lineTo(-w / 2, nose + noseLen);
  fus.bezierCurveTo(-w / 2, nose + noseLen * 0.45, -w * 0.42, nose, 0, nose);
  fus.closePath();
  body.push(fus);

  // A trapezoid surface (wing or tailplane), mirrored left/right. Returns
  // the leading-edge y at a given x, for hanging engines off it.
  const surface = (rootY: number, semi: number, rootChord: number, tipChord: number, sweepDeg: number) => {
    const rootX = w * 0.45;
    const tipLE = rootY + Math.max(semi - rootX, 0) * Math.tan(rad(sweepDeg));
    for (const side of [1, -1]) {
      const p = new Path2D();
      p.moveTo(side * rootX, rootY);
      p.lineTo(side * semi, tipLE);
      p.lineTo(side * semi, tipLE + tipChord);
      p.lineTo(side * rootX, rootY + rootChord);
      p.closePath();
      body.push(p);
    }
    return (x: number) => rootY + Math.max(Math.abs(x) - rootX, 0) * Math.tan(rad(sweepDeg));
  };

  const wingLE = surface(nose + a.wingAt * L, B / 2, a.rootChord * L, a.tipChord * L, a.sweep);

  // Horizontal tail: right at the end for T-tails, a little forward otherwise.
  const props = a.family === 'turboprop' || a.family === 'quadProp' || a.family === 'single';
  const tailRootChord = L * (props ? 0.11 : 0.085);
  const tailRootY = tail - tailRootChord - L * (a.tTail ? 0.015 : 0.06);
  const tailSweep = props ? 6 : Math.min(a.sweep + 6, 42);
  surface(tailRootY, Math.max((a.tailSpan * B) / 2, w * 0.9), tailRootChord, tailRootChord * 0.42, tailSweep);

  const nacelle = (cx: number, front: number, dia: number, len: number) => {
    const p = new Path2D();
    p.roundRect(cx - dia / 2, front, dia, len, Math.min(dia / 2, len / 2));
    body.push(p);
    details.push(p);
  };
  const propBar = (cx: number, y: number, dia: number) => {
    const t = Math.max(0.28 * k, 1.3);
    const p = new Path2D();
    p.roundRect(cx - dia / 2, y - t / 2, dia, t, t / 2);
    body.push(p);
  };

  for (const e of a.engines) {
    const dia = Math.max(e.dia * k, 1.6);
    const len = Math.max(e.len * k, 2.4);
    for (const side of [1, -1]) {
      const cx = side * e.at * (B / 2);
      const le = wingLE(cx);
      if (a.family === 'overWing') {
        nacelle(cx, le + a.rootChord * L * 0.25, dia, len); // on pylons above the wing, set back
      } else if (props) {
        const front = le - len * 0.4;
        nacelle(cx, front, dia, len);
        if (a.prop) propBar(cx, front - 0.15 * k, a.prop * k);
      } else {
        nacelle(cx, le - len * 0.5, dia, len);
      }
    }
  }

  // Rear fuselage-mounted engines (regional and business jets, MD-80, 717).
  if (a.family === 'aftTwin' || a.family === 'aftTri') {
    const dia = Math.max(a.fuselage * k * 0.55, 1.6);
    const len = Math.max(dia * 2.1, 3);
    const front = tail - L * 0.36;
    for (const side of [1, -1]) nacelle(side * (w / 2 + dia * 0.45), front, dia, len);
    if (a.family === 'aftTri') nacelle(0, front + len * 0.15, dia * 0.9, len * 0.95);
  }
  // Cirrus Vision Jet: one engine on top of the rear fuselage.
  if (a.family === 'topJet') {
    const dia = Math.max(a.fuselage * k * 0.6, 1.6);
    nacelle(0, tail - L * 0.42, dia, dia * 2.2);
  }
  // Tri-jets: the third engine sits in the tail, on the centreline.
  if (a.family === 'trijet' && a.engines[0]) {
    const dia = a.engines[0].dia * k;
    nacelle(0, tail - L * 0.2, dia * 1.05, dia * 2.2);
  }
  // Single-engine: propeller at the nose.
  if (a.family === 'single' && a.prop) propBar(0, nose - 0.1 * k, a.prop * k);

  return { body, details };
}

function makeIcon(a: Airframe, color: string, outline: string, glow: string | null): ImageData {
  const canvas = document.createElement('canvas');
  canvas.width = ICON_PX;
  canvas.height = ICON_PX;
  const ctx = canvas.getContext('2d')!;
  ctx.translate(ICON_PX / 2, ICON_PX / 2);
  const legacy = LEGACY[a.family];

  if (legacy) {
    // Hand-drawn shapes: single-path pipeline, scaled to the same span.
    const s = ICON_SPAN_PX / legacy.extent;
    ctx.save();
    ctx.translate(1.5, 2.2);
    legacy.draw(ctx, s);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
    ctx.fill();
    ctx.restore();
    legacy.draw(ctx, s);
    ctx.shadowColor = glow ?? 'rgba(0, 0, 0, 0.45)';
    ctx.shadowBlur = glow ? 9 : 4;
    ctx.shadowOffsetY = glow ? 0 : 1.5;
    ctx.fillStyle = color;
    ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.strokeStyle = outline;
    ctx.lineWidth = 2.4;
    ctx.lineJoin = 'round';
    ctx.stroke();
  } else {
    const { body, details } = airframeParts(a);
    // 1. Extruded underside: a dark offset copy behind the lit body.
    ctx.save();
    ctx.translate(1.5, 2.2);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
    for (const p of body) ctx.fill(p);
    ctx.restore();
    // 2. Drop shadow or selection glow under the whole union.
    ctx.save();
    ctx.shadowColor = glow ?? 'rgba(0, 0, 0, 0.45)';
    ctx.shadowBlur = glow ? 9 : 4;
    ctx.shadowOffsetY = glow ? 0 : 1.5;
    ctx.fillStyle = outline;
    for (const p of body) ctx.fill(p);
    ctx.restore();
    // 3. Outline: stroke every piece, then fill over it so only the outer edge remains.
    ctx.strokeStyle = outline;
    ctx.lineWidth = 2.6;
    ctx.lineJoin = 'round';
    for (const p of body) ctx.stroke(p);
    ctx.fillStyle = color;
    for (const p of body) ctx.fill(p);
    // 4. Nacelles outlined on top of the wing so they read as engines.
    ctx.strokeStyle = outline;
    ctx.globalAlpha = 0.6;
    ctx.lineWidth = 1;
    for (const p of details) ctx.stroke(p);
    ctx.globalAlpha = 1;
  }

  // Beveled highlight/shade, confined to the silhouette's own pixels via
  // `source-atop`: a top-left light source sweeping to a darker trailing edge.
  ctx.globalCompositeOperation = 'source-atop';
  const h = ICON_PX / 2;
  const grad = ctx.createLinearGradient(-h * 0.8, -h * 0.9, h * 0.6, h * 0.75);
  grad.addColorStop(0, 'rgba(255, 255, 255, 0.5)');
  grad.addColorStop(0.45, 'rgba(255, 255, 255, 0)');
  grad.addColorStop(1, 'rgba(0, 0, 0, 0.32)');
  ctx.fillStyle = grad;
  ctx.fillRect(-h, -h, ICON_PX, ICON_PX);
  ctx.globalCompositeOperation = 'source-over';
  return ctx.getImageData(0, 0, ICON_PX, ICON_PX);
}

/** Image id for an airframe in a colour variant: altitude bucket, 'sel' or 'emg'. */
export function iconId(key: string, variant: number | 'sel' | 'emg'): string {
  return `ac|${key}|${variant}`;
}

/**
 * Generate aircraft icons on demand: MapLibre asks for each `ac|type|variant`
 * image the first time a feature uses it. `outline` matches the base style.
 */
export function registerPlaneIcons(
  map: {
    addImage(name: string, img: ImageData, opts?: { pixelRatio?: number }): void;
    hasImage(name: string): boolean;
    on(type: 'styleimagemissing', fn: (e: { id: string }) => void): unknown;
  },
  outline: string,
): void {
  map.on('styleimagemissing', (e) => {
    if (!e.id.startsWith('ac|') || map.hasImage(e.id)) return;
    const [, key, variant] = e.id.split('|');
    const a = airframeByKey(key);
    if (!a) return;
    const img =
      variant === 'sel'
        ? makeIcon(a, '#ffffff', '#ffb020', 'rgba(255, 176, 32, 0.9)')
        : variant === 'emg'
          ? makeIcon(a, '#ff2d2d', '#ffffff', 'rgba(255, 30, 30, 0.95)')
          : makeIcon(a, bucketColor(Number(variant)), outline, null);
    map.addImage(e.id, img, { pixelRatio: 1 });
  });
}

const pictureCache = new Map<string, string>();

/** The airframe's silhouette as an image URL (for the flight panel), drawn like the map icon. */
export function airframePicture(key: string, color = '#d9dde3', outline = '#12121e'): string | null {
  const id = `${key}|${color}`;
  const cached = pictureCache.get(id);
  if (cached) return cached;
  const a = airframeByKey(key);
  if (!a) return null;
  const canvas = document.createElement('canvas');
  canvas.width = ICON_PX;
  canvas.height = ICON_PX;
  canvas.getContext('2d')!.putImageData(makeIcon(a, color, outline, null), 0, 0);
  const url = canvas.toDataURL('image/png');
  pictureCache.set(id, url);
  return url;
}
